import { HttpService, type IHttpClientModel } from "@ir/core/http";
import { AxiosError } from "axios";
import { createLogger } from "@src/infrastructure/services/logger";
import type { ILogger } from "@src/infrastructure/services/logger";
import { AppConfig } from "@src/config/AppConfig";

const _appConfig = AppConfig

const API_BASE_URL = _appConfig.getApiUrl();
const TIMEOUT = 10000;
const TIMESTAMP = Date.now();
const TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * @example
 * const NetworkManager = new NetworkManager();
 * NetworkManager.get<ResponseType>("/path/to/api", { query: "params" });
 * NetworkManager.post<ResponseType>("/path/to/api", { data: "params" });
 * NetworkManager.put<ResponseType>("/path/to/api", { data: "params" });
 * NetworkManager.delete<ResponseType>("/path/to/api", { query: "params" });
 */

export class NetworkManager {
  public readonly httpService: HttpService;
  private readonly networkConfig: IHttpClientModel;
  private readonly logger: ILogger = createLogger("NetworkManager");

  constructor(_networkConfig?: Omit<IHttpClientModel, "baseURL">) {
    this.networkConfig = {
      baseURL: API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      timeout: TIMEOUT,
      ..._networkConfig,
    };
    this.httpService = new HttpService(this.networkConfig);
    this.handleRequest();
    this.handleResponse();
  }

  protected handleRequest() {
    const _httpService = this.httpService;
    if (_httpService.interceptors.request) {
      _httpService.interceptors.request.use(config => {
        const defaultData = {
          timestamp: TIMESTAMP,
          timezone: TIMEZONE,
        };
        if (config.method === "get") {
          config.transformRequest = data => {
            return {
              ...data,
              ...defaultData,
            };
          };
        } else if (config.data instanceof FormData) {
            config.data.append("timestamp", TIMESTAMP.toString());
            config.data.append("timezone", TIMEZONE);
          } else {
            config.data = {
              ...config.data,
              ...defaultData,
            };
          }
        return config;
      });
    }
  }

  protected handleResponse() {
    this.httpService.interceptors.response.use(
      response => {
        return response.data;
      },
      (error: AxiosError<{ message?: string; status?: string }>) => {

        const getErrorMessage = (error: AxiosError<{ message?: string; status?: string }>) => {
          if (error.response) {
            return error.response.data?.message ?? error.message;
          }
          if (error.request) {
            return error.message;
          }
          if (error.message === "Network Error") {
            return "Network error detected. Are you offline?";
          }
          return "Something went wrong";
        };

        const errorMessage = getErrorMessage(error);

        if (error.response) {
          switch (error.response.status) {
            case 400:
              this.logger.error("Bad Request", error);
              break;
            case 401:
              this.logger.error("Unauthorized", error);
              break;
            case 403:
              this.logger.error("Forbidden", error);
              break;
            case 404:
              this.logger.error("Not Found", error);
              break;
            case 500:
              this.logger.error("Internal Server Error", error);
              break;
            default:
              this.logger.error("Something went wrong", error);
          }
        } else if (error.request) {
          this.logger.error("No Response", error);
        } else if (error.message === "Network Error") {
          this.logger.error("Network Error", error);
        } else {
          this.logger.error("Something went wrong", error );
        }
        return Promise.reject(new Error(errorMessage));
      }
    );
  }

  public async post<T>(url: string, data?: unknown, params?: Record<string, unknown>) {
    return this.httpService.post<unknown, T>(url, data, params);
  }

  public async get<T>(url: string, params?: Record<string, unknown>) {
    return this.httpService.get<T>(url, params);
  }

  public async put<T>(url: string, data?: unknown, params?: Record<string, unknown>) {
    return this.httpService.put<unknown, T>(url, data, params);
  }

  public async delete<T>(url: string, params?: Record<string, unknown>) {
    return this.httpService.delete<T>(url, params);
  }
}
